import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Box, Stack, Text, Draft } from '@resi-media/resi-ui';
import { useLocalStorage } from 'usehooks-ts';
import { useMockData } from '@preview/contexts/MockDataContext';
import { useRefresh } from '@preview/contexts/RefreshContext';
import type { SubSplash } from '@preview/types/subsplash';

const PAGE_SIZE = 100;

const chunkArray = <T,>(arr: T[], count: number): T[][] => {
  const size = Math.ceil(arr.length / count);
  return Array.from({ length: count }, (_, i) => arr.slice(i * size, (i + 1) * size)).filter(
    (chunk) => chunk.length > 0
  );
};

const SubSplashDataFetcherCard: React.FC = () => {
  const [orgPath, setOrgPath] = useLocalStorage('preview-subsplash-org-path', '');
  const [maxVideos, setMaxVideos] = useLocalStorage('preview-subsplash-max-videos', 20);
  const [playlistCount, setPlaylistCount] = useLocalStorage('preview-subsplash-playlist-count', 1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { addPlaylistMock, addSiteMock, addVideoMock, clearAllMocks, embedId, resetPendingChanges } = useMockData();
  const { triggerRefresh } = useRefresh();

  const parseOrgAndTab = (raw: string): { org: string; tab: string } => {
    const trimmed = raw.trim();
    const urlStr = /^https?:\/\//i.test(trimmed) ? trimmed : `https://subsplash.com/${trimmed}`;
    let url: URL;
    try {
      url = new URL(urlStr);
    } catch {
      throw new Error(`Invalid SubSplash URL or org path: ${raw}`);
    }

    const parts = url.pathname.replace(/^\//, '').split('/');
    const org = parts[0];
    const tab = parts[1] ?? 'teaching';

    if (!org) throw new Error('Could not determine org from URL');
    return { org, tab };
  };

  const getGuestToken = async (org: string, tab: string): Promise<string> => {
    // SubSplash uses Ember FastBoot SSR — the guest token is embedded in the HTML as:
    // <script type="fastboot/shoebox" id="shoebox-tokens">{"apiToken":"..."}</script>
    const response = await fetch(`/subsplash-page/${org}/${tab}`);
    if (!response.ok) throw new Error(`Failed to fetch SubSplash page: ${response.status}`);
    const html = await response.text();

    const shoeboxMatch = html.match(/<script[^>]+id="shoebox-tokens"[^>]*>([^<]+)<\/script>/);
    if (shoeboxMatch?.[1]) {
      try {
        const { apiToken } = JSON.parse(shoeboxMatch[1]) as { apiToken?: string };
        if (apiToken) return apiToken;
      } catch {
        // malformed JSON — fall through
      }
    }

    throw new Error(
      'Could not retrieve token automatically. Open DevTools → Network → visit your SubSplash page → copy the x-api-token response header.'
    );
  };

  const fetchAllMediaItems = async (token: string): Promise<SubSplash.MediaItem[]> => {
    const items: SubSplash.MediaItem[] = [];
    let page = 1;

    while (items.length < maxVideos) {
      const params = new URLSearchParams({
        'filter[status]': 'published',
        include: 'images,video.video-outputs',
        'page[size]': PAGE_SIZE.toString(),
        'page[number]': page.toString(),
        sort: '-date',
      });

      const response = await fetch(`/subsplash-api/media/v1/media-items?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`SubSplash media API error: ${response.status}`);

      const data: SubSplash.MediaItemsResponse = await response.json();
      const pageItems = data._embedded['media-items'];
      items.push(...pageItems);

      if (pageItems.length < PAGE_SIZE || !data._links.next) break;
      page++;
    }

    return items.slice(0, maxVideos);
  };

  const fetchSubSplashData = async () => {
    if (!orgPath.trim()) {
      setError('SubSplash org path or URL is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { org, tab } = parseOrgAndTab(orgPath);
      const token = await getGuestToken(org, tab);
      const mediaItems = await fetchAllMediaItems(token);

      if (mediaItems.length === 0) {
        throw new Error('No published media items found for this org');
      }

      clearAllMocks();

      const siteId = `site-subsplash-${org}`;
      const basePlaylistId = `subsplash-${org}`;

      const videos = mediaItems.map((item) => {
        const thumbnailUrl = item._embedded.images?.[0]?._links.related.href ?? '';
        return {
          id: item.id,
          title: item.title,
          description: item.summary_text ?? '',
          thumbnail: { url: thumbnailUrl },
          airDate: item.date,
          embedId,
          formattedDescription: `<p>${item.summary_text ?? ''}</p>`,
        };
      });

      const chunks = chunkArray(videos, Math.min(playlistCount, videos.length));

      addSiteMock(
        {
          id: siteId,
          description: `SubSplash Org: ${org}`,
          endpoint: '*sites-boot.resi.io/api/v1/sites?url=*',
          data: {
            id: siteId,
            customerId: `customer-subsplash-${org}`,
            channelId: 'preview-channel-id',
            name: `${org} - SubSplash`,
            description: 'SubSplash Content',
            url: `https://subsplash.com/${org}/${tab}`,
            branding: { mode: 'dark' },
            feeds: [
              {
                id: `feed-subsplash-${org}`,
                name: 'SubSplash Feed',
                description: 'Content from SubSplash',
                default: true,
                playlists: chunks.map((chunk, i) => ({
                  id: `${basePlaylistId}-${i + 1}`,
                  name: chunks.length > 1 ? `Media ${i + 1}` : 'All Media',
                  size: chunk.length,
                  url: `/api/playlists/${basePlaylistId}-${i + 1}`,
                })),
              },
            ],
          },
        },
        true
      );

      chunks.forEach((chunk, i) => {
        const playlistId = `${basePlaylistId}-${i + 1}`;
        const playlistName = chunks.length > 1 ? `Media ${i + 1}` : 'All Media';

        addPlaylistMock(
          {
            id: playlistId,
            endpoint: `*/api/playlists/${playlistId}*`,
            description: `SubSplash Org: ${org} (${i + 1}/${chunks.length})`,
            data: { id: playlistId, name: playlistName, videos: chunk },
          },
          true
        );

        chunk.forEach((video) => {
          addVideoMock(
            {
              id: video.id,
              description: `SubSplash Media: ${video.title}`,
              endpoint: `*/ondemand/videos/${video.id}`,
              data: {
                id: video.id,
                title: video.title,
                description: video.description,
                thumbnail: { url: video.thumbnail.url },
                airDate: video.airDate,
                embedId,
                tags: ['subsplash'],
                playlists: [{ id: playlistId, url: `*/api/playlists/${playlistId}*` }],
              },
            },
            true
          );
        });
      });

      setSuccess(`SubSplash data fetched: ${mediaItems.length} items across ${chunks.length} playlist(s).`);
      triggerRefresh();
      resetPendingChanges();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Draft.Card>
      <Draft.CardSection>
        <Stack gap="m">
          <Text variant="h3">SubSplash Data Fetcher</Text>
          <Text>Fetch media items from a SubSplash org and transform them into mock data</Text>

          <Stack gap="s">
            <Draft.FormField fieldLabel="Org Path or URL">
              <Draft.TextInput
                css={css`
                  width: 100%;
                `}
                onChange={(e) => setOrgPath(e.target.value)}
                placeholder="wearescc/teaching or https://subsplash.com/wearescc/teaching"
                value={orgPath}
              />
            </Draft.FormField>
            <Text colorVariant="secondary" variant="body3">
              Accepts an org path (e.g. wearescc/teaching) or a full SubSplash URL.
            </Text>

            <Box
              css={css`
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;
              `}
            >
              <Draft.FormField fieldLabel="Max Videos">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={500}
                  min={1}
                  onChange={(e) => setMaxVideos(parseInt(e.target.value, 10) || 20)}
                  type="number"
                  value={maxVideos.toString()}
                />
              </Draft.FormField>
              <Draft.FormField fieldLabel="Playlist Count">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={50}
                  min={1}
                  onChange={(e) => setPlaylistCount(parseInt(e.target.value, 10) || 1)}
                  type="number"
                  value={playlistCount.toString()}
                />
              </Draft.FormField>
            </Box>

            {error && <Text color="error">{error}</Text>}
            {success && <Text color="success">{success}</Text>}

            <Draft.Button
              css={css`
                margin-top: 8px;
              `}
              disabled={isLoading}
              label="Fetch SubSplash Data"
              onClick={fetchSubSplashData}
            >
              {isLoading ? 'Loading...' : 'Fetch SubSplash Data'}
            </Draft.Button>
          </Stack>
        </Stack>
      </Draft.CardSection>
    </Draft.Card>
  );
};

export default SubSplashDataFetcherCard;
